import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import AuthShell from "../components/AuthShell";
import { Button, Input } from "../components/ui";
import { useAuth } from "../context/AuthContext";
import { apiError } from "../lib/api";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", family_name: "", email: "", password: "", family_code: "" });
  const [hasCode, setHasCode] = useState(false);
  const [accept, setAccept] = useState(false);
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!accept) { toast.error("Please accept the Terms & Privacy Policy."); return; }
    setLoading(true);
    try {
      const payload = { ...form, family_code: hasCode ? form.family_code.trim() : "" };
      const data = await register(payload);
      toast.success(data?.joined ? "You've joined the family! 🐭" : "Family created! Welcome to ChoreMouse 🐭");
      navigate("/app");
    } catch (err) {
      toast.error(apiError(err));
    } finally { setLoading(false); }
  };

  return (
    <AuthShell title="Create your account" subtitle="It's free to start. Join a family or make a new one."
      footer={<>Already have an account? <Link to="/login" className="text-primary font-bold" data-testid="link-login">Sign in</Link></>}>
      <form onSubmit={submit} className="space-y-4" data-testid="register-form">
        <Input label="Your name" value={form.name} onChange={set("name")} placeholder="Alex" required data-testid="register-name" />
        <Input label="Email" type="email" value={form.email} onChange={set("email")} placeholder="you@example.com" required data-testid="register-email" />
        <Input label="Password" type="password" value={form.password} onChange={set("password")} placeholder="At least 6 characters" required minLength={6} data-testid="register-password" />

        <div className="rounded-2xl border-2 border-slate-200 p-3.5 bg-slate-50/60">
          <label className="flex items-center gap-3 text-sm font-bold text-ink-soft cursor-pointer">
            <input type="checkbox" checked={hasCode} onChange={(e) => setHasCode(e.target.checked)} className="w-5 h-5 accent-[#7C3AED]" data-testid="register-has-code" />
            <span>I have a family code (join another grown-up's family) <span className="text-ink-muted font-semibold">— optional</span></span>
          </label>
          {hasCode ? (
            <div className="mt-3">
              <Input value={form.family_code} onChange={(e) => setForm((f) => ({ ...f, family_code: e.target.value.toUpperCase().replace(/\s/g, "") }))}
                placeholder="e.g. 85MKDJ" data-testid="register-family-code" className="text-center tracking-[0.2em] font-bold uppercase" />
              <p className="text-[11px] text-ink-muted font-semibold mt-1.5">You'll join as a co-parent. The owner decides if you can make changes.</p>
            </div>
          ) : (
            <div className="mt-3">
              <Input value={form.family_name} onChange={set("family_name")} placeholder="Family name (optional) — e.g. The Garcia Family" data-testid="register-family" />
            </div>
          )}
        </div>

        <label className="flex items-start gap-3 text-sm font-semibold text-ink-soft cursor-pointer">
          <input type="checkbox" checked={accept} onChange={(e) => setAccept(e.target.checked)} className="mt-0.5 w-5 h-5 accent-[#7C3AED]" data-testid="register-accept" />
          <span>I agree to the <Link to="/terms" className="text-primary font-bold">Terms</Link> and <Link to="/privacy" className="text-primary font-bold">Privacy Policy</Link>.</span>
        </label>
        <Button type="submit" size="lg" className="w-full" disabled={loading} data-testid="register-submit">
          {loading ? "Creating…" : hasCode ? "Join the family" : "Create my family"}
        </Button>
      </form>
    </AuthShell>
  );
}
